import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { CreditCard, ArrowRight } from 'lucide-react';
import { i18n } from '../utils/i18n';
import { getBrandName } from '../utils/brand';

interface LoginProps {
  onLogin: (nickname: string, pin: string) => Promise<void>;
  onSwitchToRegister: () => void;
  isLoading: boolean;
}

export function Login({ onLogin, onSwitchToRegister, isLoading }: LoginProps) {
  const [nickname, setNickname] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nickname.trim() || !pin.trim()) {
      toast.error(i18n.t('pleaseEnterBoth'));
      return;
    }

    try {
      await onLogin(nickname, pin);
      toast.success(i18n.t('welcomeBack'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('loginFailed'));
    }
  };

  return (
    <div className="h-full bg-background flex flex-col justify-center p-6">
      {/* Logo */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl mb-4">
          <CreditCard className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-4xl font-light text-foreground tracking-wide">{getBrandName()}</h1>
        <p className="text-muted-foreground text-base mt-1">Digital Wallet</p>
      </div>

      {/* Login Form */}
      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={i18n.t('usernamePlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={2}
              maxLength={10}
            />
          </div>
          
          <div>
            <Input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={i18n.t('pinPlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={6}
              maxLength={10}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 mt-8 bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium rounded-xl flex items-center justify-center gap-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <span>{i18n.t('signIn')}</span>
                <ArrowRight size={20} />
              </>
            )}
          </Button>
        </form>



        {/* Register Link */}
        <div className="text-center pt-2">
          <p className="text-muted-foreground text-sm mb-4">{i18n.t('dontHaveAccount')}</p>
          <Button 
            onClick={onSwitchToRegister}
            variant="outline"
            className="w-full h-12 border-border text-foreground bg-transparent hover:bg-muted text-base"
            disabled={isLoading}
          >
            {i18n.t('createAccount')}
          </Button>
        </div>
      </div>
    </div>
  );
}